/**
 * Add Branch Page
 * Form to create a new branch location
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import branchService from '../../services/branches';
import LocationPicker from '../../Components/LocationPicker/LocationPicker';
import './AddBranch.css';

const AddBranch = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get branch ID from URL for edit mode
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'Branch',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
    },
    latitude: '',
    longitude: '',
    contact: {
      phone: '',
      email: '',
      manager: '',
    },
    notes: '',
  });

  const fetchBranchData = useCallback(async () => {
    try {
      setFetchLoading(true);
      setError(null);
      const branch = await branchService.getBranchById(id);

      // Populate form with existing branch data
      setFormData({
        name: branch.name || '',
        code: branch.code || '',
        type: branch.type || 'Branch',
        address: {
          street: branch.address?.street || '',
          city: branch.address?.city || '',
          state: branch.address?.state || '',
          zipCode: branch.address?.zipCode || '',
          country: branch.address?.country || 'USA',
        },
        latitude: branch.latitude?.toString() || '',
        longitude: branch.longitude?.toString() || '',
        contact: {
          phone: branch.contact?.phone || '',
          email: branch.contact?.email || '',
          manager: branch.contact?.manager || '',
        },
        notes: branch.notes || '',
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load branch data');
      console.error('Error fetching branch:', err);
    } finally {
      setFetchLoading(false);
    }
  }, [id]);

  // Fetch branch data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      fetchBranchData();
    }
  }, [isEditMode, id, fetchBranchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value
      }
    }));
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      contact: {
        ...prev.contact,
        [name]: value
      }
    }));
  };

  const handleLocationChange = useCallback((lat, lng) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.name || !formData.code || !formData.address.street ||
          !formData.address.city || !formData.address.state || !formData.address.zipCode ||
          !formData.latitude || !formData.longitude || !formData.contact.phone) {
        throw new Error('Please fill in all required fields');
      }

      // Convert latitude and longitude to numbers
      const branchData = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
      };

      if (isEditMode) {
        // Update existing branch
        await branchService.updateBranch(id, branchData);
      } else {
        // Create new branch
        await branchService.createBranch(branchData);
      }

      navigate('/branches');
    } catch (err) {
      setError(err.response?.data?.message || err.message || `Failed to ${isEditMode ? 'update' : 'create'} branch`);
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} branch:`, err);
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while fetching branch data
  if (fetchLoading) {
    return (
      <div className="add-branch-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading branch data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="add-branch-page">
      <div className="page-header">
        <div>
          <h1>{isEditMode ? 'Edit Branch' : 'Add New Branch'}</h1>
          <p className="page-subtitle">
            {isEditMode ? 'Update branch location information' : 'Create a new branch location for your company'}
          </p>
        </div>
        <button className="btn-secondary" onClick={() => navigate('/branches')}>
          Cancel
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="branch-form">
        {/* Basic Information */}
        <div className="form-section">
          <h2 className="section-title">Basic Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Branch Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Downtown Branch"
              />
            </div>

            <div className="form-group">
              <label htmlFor="code">Branch Code *</label>
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                required
                maxLength="10"
                placeholder="e.g., DTB"
                style={{ textTransform: 'uppercase' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="type">Branch Type *</label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
              >
                <option value="Branch">Branch</option>
                <option value="Main">Main</option>
                <option value="Hub">Hub</option>
                <option value="Warehouse">Warehouse</option>
              </select>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="form-section">
          <h2 className="section-title">Address Information</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="street">Street Address *</label>
              <input
                type="text"
                id="street"
                name="street"
                value={formData.address.street}
                onChange={handleAddressChange}
                required
                placeholder="e.g., 123 Main Street"
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">City *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.address.city}
                onChange={handleAddressChange}
                required
                placeholder="e.g., New York"
              />
            </div>

            <div className="form-group">
              <label htmlFor="state">State *</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.address.state}
                onChange={handleAddressChange}
                required
                placeholder="e.g., NY"
              />
            </div>

            <div className="form-group">
              <label htmlFor="zipCode">Zip Code *</label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.address.zipCode}
                onChange={handleAddressChange}
                required
                placeholder="e.g., 10001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="country">Country *</label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.address.country}
                onChange={handleAddressChange}
                required
                placeholder="e.g., USA"
              />
            </div>
          </div>
        </div>

        {/* Location Map */}
        <div className="form-section">
          <LocationPicker
            latitude={formData.latitude}
            longitude={formData.longitude}
            onLocationChange={handleLocationChange}
          />
        </div>

        {/* Contact Information */}
        <div className="form-section">
          <h2 className="section-title">Contact Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.contact.phone}
                onChange={handleContactChange}
                required
                placeholder="e.g., +1-555-0100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.contact.email}
                onChange={handleContactChange}
                placeholder="e.g., branch@company.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="manager">Manager Name</label>
              <input
                type="text"
                id="manager"
                name="manager"
                value={formData.contact.manager}
                onChange={handleContactChange}
                placeholder="e.g., John Doe"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="form-section">
          <h2 className="section-title">Additional Notes</h2>
          <div className="form-group full-width">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Any additional information about this branch..."
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/branches')}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Branch' : 'Create Branch')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBranch;
